import { useEffect, useState } from "react";
import { useMediaQuery } from "react-responsive";
import QuestionBubble from "../components/question/QuestionBubble.tsx";
import QuestionPageLayout from "../components/question/QuestionPageLayout.tsx";
import { useRandomCharacterImage } from "../hooks/useRandomCharacterImage.ts";

interface QuestionFillInTheBlankPageProps {
  hearts: number;
  onNext: () => void;
  onBack: () => void;
  progress: number;
  isLast: boolean;
  onValidate?: () => void;
  onAnswer?: (xp: number, correct: number, total: number) => void;
  onIncorrectAnswer?: () => void;
  darkMode?: boolean;
  questionConfig?: any;
  vocabularyBank?: any[];
  lessonData?: any;
}

const QuestionFillInTheBlankPage = ({
  hearts,
  onNext,
  onBack,
  progress,
  isLast,
  onValidate,
  onAnswer,
  onIncorrectAnswer,
  darkMode = false,
  questionConfig,
  vocabularyBank,
  lessonData,
}: QuestionFillInTheBlankPageProps) => {
  // Create stable dependency values
  const questionId = questionConfig?.correctAnswer;
  const questionSentenceStr = questionConfig?.questionSentence?.join(',');
  const characterImg = useRandomCharacterImage([questionId, questionSentenceStr]);
  // Use lesson-specific data or fallback
  const wordDict = vocabularyBank ?
    Object.fromEntries(vocabularyBank.map((w: any) => [w.id, w])) : {};

  console.log('🔤 FillInBlank Debug:');
  console.log('📋 questionConfig:', questionConfig);
  console.log('📚 vocabularyBank length:', vocabularyBank?.length || 0);
  console.log('🔍 wordDict keys:', Object.keys(wordDict));

  const options = questionConfig?.options?.map((option: any) => {
    const word = wordDict[option.id];
    if (!word) {
      console.warn(`⚠️ Option word ID ${option.id} not found in vocabulary bank`);
    }
    return {
      id: option.id,
      text: word?.batak || option.text || "",
    };
  }) || [];

  console.log('✅ options:', options);

  const correctAnswerId = questionConfig?.correctAnswer || (options[0]?.id || 0);
  console.log('🎯 correctAnswerId:', correctAnswerId);

  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [showPopup, setShowPopup] = useState<string | false>(false);
  const [checked, setChecked] = useState(false);

  // ...existing code...

  // Reset state when question changes
  useEffect(() => {
    console.log('🔄 FillInBlank Question changed, resetting state');
    setSelectedAnswer(null);
    setChecked(false);
    setShowPopup(false);
  }, [questionId, questionSentenceStr]);

  console.log('🎮 FillInBlank Current state - selectedAnswer:', selectedAnswer, 'checked:', checked);

  // Use lesson-specific question sentence or fallback
  const quest_batak = questionConfig?.questionSentence || [105, 999]; // fallback with blank at position 1

  // Fill the blank with selected option if any
  const wordsForQuestion = quest_batak.map((id: number) => {
    if (id === 999 && selectedAnswer) {
      const selectedWord = vocabularyBank?.find((w: any) => w.id === selectedAnswer);
      return {
        id: 999,
        batak: selectedWord?.batak || "",
        syllable_mapping: selectedWord?.syllable_mapping || [{ batak_char: "", latin: "" }],
        latin: selectedWord?.latin || [""],
        indonesia: selectedWord?.indonesia || "",
      };
    }
    return wordDict[id] || { id, batak: "", latin: [""], indonesia: "", syllable_mapping: [] };
  });

  // Call onValidate after checking
  const handleCheck = () => {
    setChecked(true);
    if (onValidate) onValidate();
    // Call onAnswer with true if correct, false if not
    if (onAnswer && selectedAnswer !== null) {
      const isCorrect = selectedAnswer === correctAnswerId;
      onAnswer(isCorrect ? 10 : 0, isCorrect ? 1 : 0, 1); // (xp, correct, total)

      // Reduce heart if answer is incorrect
      if (!isCorrect && onIncorrectAnswer) {
        onIncorrectAnswer();
      }
    }
  };

  const isMobile = useMediaQuery({ maxWidth: 767 });
  return (
    <QuestionPageLayout
      hearts={hearts}
      onBack={onBack}
      progress={progress}
      darkMode={darkMode}
    >
      <h1
        className={`${isMobile ? "text-lg mb-4" : "text-2xl mb-8"} font-bold text-center ${darkMode ? "text-gray-100" : "text-gray-800"}`}
        style={{ fontFamily: "Feather Bold" }}
      >
        Lengkapilah kalimat berikut!
      </h1>

      <div className={`flex flex-col items-center ${isMobile ? "mb-5" : "mb-10"}`}>
        <div className={`flex items-center justify-center ${isMobile ? "gap-3 mb-5" : "gap-6 mb-10"}`}>
          <img
            src={characterImg}
            alt="Characters"
            className={isMobile ? "w-20 h-20 object-contain" : "w-32 h-32 object-contain"}
          />
          <QuestionBubble
            words={wordsForQuestion}
            showPopup={showPopup}
            setShowPopup={setShowPopup}
            darkMode={darkMode}
          />
        </div>
      </div>

      <div className={`grid grid-cols-2 gap-3 mb-8 w-full max-w-md mx-auto`}>
        {options.map((option) => {
          let optionClass = darkMode
            ? "bg-gray-800 border-gray-700 text-gray-100 hover:bg-gray-800"
            : "bg-white border-gray-300 hover:bg-gray-50";
          if (checked) {
            if (option.id === correctAnswerId) {
              optionClass = darkMode
                ? "bg-lime-800/90 border-lime-400 text-lime-200"
                : "bg-lime-100 border-lime-500 text-lime-700";
            } else if (selectedAnswer === option.id) {
              optionClass = darkMode
                ? "bg-red-800/90 border-red-400 text-red-200"
                : "bg-red-100 border-red-500 text-red-700";
            }
          } else if (selectedAnswer === option.id) {
            optionClass = darkMode
              ? "bg-blue-900 border-blue-400 text-blue-200"
              : "bg-blue-100 border-blue-300";
          }
          return (
            <button
              key={option.id}
              onClick={() => {
                if (!checked) setSelectedAnswer(option.id);
              }}
              className={`border rounded-lg ${isMobile ? "py-2 px-3 text-base" : "py-4 px-6 text-xl"} text-center transition-colors ${optionClass}`}
              disabled={checked}
            >
              <div
                className={isMobile ? "text-base" : "text-xl"}
                style={{ fontFamily: "'Noto Sans Batak'" }}
              >
                {option.text}
              </div>
            </button>
          );
        })}
      </div>

      <div className="text-center mt-6">
        {!checked ? (
          <button
            onClick={handleCheck}
            disabled={selectedAnswer === null}
            className={`rounded-lg font-bold transition-colors ${isMobile ? "px-5 py-2 text-sm" : "px-8 py-3 text-base"} ${
              selectedAnswer === null
                ? darkMode
                  ? "bg-gray-700 text-gray-300 cursor-not-allowed"
                  : "bg-gray-400 text-white cursor-not-allowed"
                : darkMode
                ? "bg-blue-700 hover:bg-blue-800 text-white"
                : "bg-blue-500 hover:bg-blue-600 text-white"
              }`}
            style={{ fontFamily: "DIN Next Rounded" }}
          >
            Periksa
          </button>
        ) : (
          <button
            onClick={onNext}
            className={`rounded-lg font-bold transition-colors ${isMobile ? "px-5 py-2 text-sm" : "px-8 py-3 text-base"} ${
              darkMode
                ? "bg-lime-600 hover:bg-lime-700 text-white"
                : "bg-lime-500 hover:bg-lime-600 text-white"
            }`}
            style={{ fontFamily: "DIN Next Rounded" }}
          >
            {isLast ? "Selesai" : "Lanjutkan"}
          </button>
        )}
      </div>
    </QuestionPageLayout>
  );
};

export default QuestionFillInTheBlankPage;
